import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtNum, curSym, fmtDate } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { exportToCsv } from "@finance/lib/exportCsv";
import { Button } from "@finance/components/ui/button";
import { DownloadSimple, Warning, ArrowRight, Clock, CheckCircle } from "@phosphor-icons/react";
import { KpiStrip, KpiCard, EmptyState } from "@finance/components/DesignSystem";

const GROUPS = [
  { key: "overdue", label: "Overdue", tone: "text-red-600", bg: "bg-red-50" },
  { key: "awaiting", label: "Awaiting", tone: "text-amber-600", bg: "bg-amber-50" },
  { key: "received", label: "Received", tone: "text-emerald-600", bg: "bg-emerald-50" },
];

export default function EfdPending() {
  const [reg, setReg] = useState({ overdue: [], awaiting: [], received: [], counts: {} });
  const [sortOwner, setSortOwner] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/expenses-efd-register").then((r) => setReg(r.data)).catch(() => {});
  }, []);

  const sortItems = (items, key) => {
    const a = [...items];
    if (sortOwner) a.sort((x, y) => (x.follow_up_owner || "").localeCompare(y.follow_up_owner || ""));
    else if (key === "overdue") a.sort((x, y) => (y.days_overdue || 0) - (x.days_overdue || 0));
    return a;
  };

  const cols = [
    { key: "ref", label: "Reference" }, { key: "supplier", label: "Supplier" },
    { key: "follow_up_owner", label: "Follow-up owner" }, { key: "due_by", label: "Due by" },
    { key: "days_overdue", label: "Days overdue" }, { key: "amount", label: "Amount", format: (v) => fmtNum(v) },
  ];
  const allExp = [...reg.overdue, ...reg.awaiting, ...reg.received];

  return (
    <Layout>
      <PageHeader title="Pending Expense EFD"
        subtitle="Expense EFD compliance worklist — applicable expenses grouped Overdue / Awaiting / Received, with days overdue and follow-up owner."
        actions={
          <>
            <Button variant="outline" data-testid="efd-sort-owner" onClick={() => setSortOwner((v) => !v)}>{sortOwner ? "Sort by days overdue" : "Sort by owner"}</Button>
            <Button variant="outline" data-testid="export-efd-pending" onClick={() => exportToCsv("pending-expense-efd", allExp, cols)}><DownloadSimple size={16} className="mr-1" />Export</Button>
          </>
        }
      />
      <div className="space-y-6 p-8">
        <KpiStrip cols={3}>
          <KpiCard icon={Warning} label="Overdue" value={reg.counts?.overdue || 0} tone="warning" testid="kpi-efd-overdue" />
          <KpiCard icon={Clock} label="Awaiting" value={reg.counts?.awaiting || 0} testid="kpi-efd-awaiting" />
          <KpiCard icon={CheckCircle} label="Received" value={reg.counts?.received || 0} tone="success" testid="kpi-efd-received" />
        </KpiStrip>

        {GROUPS.map((g) => {
          const items = sortItems(reg[g.key] || [], g.key);
          if (!items.length) return null;
          return (
            <div key={g.key} className="overflow-hidden rounded-sm border border-gray-200 bg-white" data-testid={`efd-group-${g.key}`}>
              <div className={`flex items-center justify-between border-b border-gray-200 ${g.bg} px-5 py-2.5`}>
                <span className={`font-display font-bold ${g.tone}`}>{g.label}</span>
                <span className="lx-num text-sm font-medium text-gray-500">{items.length}</span>
              </div>
              <table className="w-full text-sm">
                <thead><tr className="lx-label border-b border-gray-100 text-gray-400">
                  <th className="px-5 py-2 text-left">Reference</th>
                  <th className="px-5 py-2 text-left">Supplier</th>
                  <th className="px-5 py-2 text-left">Follow-up owner</th>
                  <th className="px-5 py-2 text-left">Due by</th>
                  {g.key === "overdue" && <th className="px-5 py-2 text-right">Days overdue</th>}
                  <th className="px-5 py-2 text-right">Amount</th>
                  <th className="px-5 py-2 text-right">Action</th>
                </tr></thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.id} data-testid={`efd-row-${r.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-5 py-2 font-medium">{r.ref || "—"}{r.paid ? <span className="ml-1 rounded-sm bg-gray-100 px-1 text-[10px] text-gray-500">Paid</span> : null}</td>
                      <td className="px-5 py-2">{r.supplier || "—"}</td>
                      <td className="px-5 py-2">{r.follow_up_owner || "—"}</td>
                      <td className="px-5 py-2 lx-num">{r.due_by ? fmtDate(r.due_by) : "—"}</td>
                      {g.key === "overdue" && <td className="px-5 py-2 text-right lx-num font-semibold text-red-600" data-testid={`efd-days-${r.id}`}>{r.days_overdue}</td>}
                      <td className="px-5 py-2 text-right lx-num">{curSym(r.currency)} {fmtNum(r.amount)}</td>
                      <td className="px-5 py-2 text-right">
                        {g.key !== "received"
                          ? <button className="inline-flex items-center gap-1 rounded-sm bg-[#1B4383] px-2 py-1 text-xs font-medium text-white hover:bg-[#122E5C]" data-testid={`efd-fix-${r.id}`} onClick={() => navigate(`/expenses?efd=${r.id}`)}>Add EFD<ArrowRight size={12} /></button>
                          : <span className="text-xs text-emerald-600">{r.efd_code || "✓"}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}

        {allExp.length === 0 && (
          <EmptyState icon={Warning} testid="efd-pending-empty" title="All caught up" message="Every applicable expense has its EFD recorded." />
        )}
      </div>
    </Layout>
  );
}
